import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Download, X, Smartphone } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const PWAInstallPrompt = () => {
  const { language } = useLanguage();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone || 
                         document.referrer.includes('android-app://');
    
    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      console.log('PWA: beforeinstallprompt event fired');
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      
      // Show install prompt after user has interacted with the app
      setTimeout(() => {
        const hasSeenPrompt = localStorage.getItem('pwa-install-prompt-seen');
        const lastShown = localStorage.getItem('pwa-install-prompt-last-shown');
        const now = Date.now();
        const oneDayAgo = now - (24 * 60 * 60 * 1000); // 24 hours
        
        console.log('PWA: Checking if should show prompt. HasSeen:', hasSeenPrompt, 'LastShown:', lastShown);
        
        // Show prompt if never seen, or if last shown was more than 24 hours ago
        if (!hasSeenPrompt || (lastShown && parseInt(lastShown) < oneDayAgo)) {
          setShowPrompt(true);
          localStorage.setItem('pwa-install-prompt-last-shown', now.toString());
          console.log('PWA: Showing install prompt');
        }
      }, 3000); // Reduced to 3 seconds for faster appearance
    };

    // Listen for successful installation
    const handleAppInstalled = () => {
      console.log('PWA: App installed successfully');
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    };

    // Force show prompt for mobile devices and first-time users
    const forceShowPrompt = () => {
      const hasSeenPrompt = localStorage.getItem('pwa-install-prompt-seen');
      const lastShown = localStorage.getItem('pwa-install-prompt-last-shown');
      const now = Date.now();
      const oneDayAgo = now - (24 * 60 * 60 * 1000);
      
      // Always show for first-time users, or if last shown > 24 hours ago
      if (!hasSeenPrompt || (lastShown && parseInt(lastShown) < oneDayAgo) || !deferredPrompt) {
        console.log('PWA: Force showing install prompt for mobile/fallback');
        setTimeout(() => {
          setShowPrompt(true);
          localStorage.setItem('pwa-install-prompt-last-shown', now.toString());
        }, 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    
    // Show prompt for all users, especially mobile
    forceShowPrompt();

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [deferredPrompt]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Fallback for iOS or other browsers
      setShowPrompt(false);
      alert(language === 'en' 
        ? 'To install this app on iOS, tap the Share button and select "Add to Home Screen"'
        : 'Para instalar esta app en iOS, toca el botón Compartir y selecciona "Añadir a pantalla de inicio"'
      );
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }
    
    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setShowPrompt(false);
    localStorage.setItem('pwa-install-prompt-seen', 'true');
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-prompt-seen', 'true');
    localStorage.setItem('pwa-install-prompt-last-shown', Date.now().toString());
  };

  if (isInstalled || !showPrompt) {
    return null;
  }

  const content = {
    en: {
      title: 'Install Redwood Conecta',
      subtitle: 'Get the app for easier access',
      description: 'Install our app for offline access, faster loading, and a native app experience.',
      install: 'Install App',
      dismiss: 'Maybe Later',
      features: [
        'Work offline',
        'Faster loading',
        'App-like experience',
        'Easy access from home screen'
      ]
    },
    es: {
      title: 'Instalar Redwood Conecta',
      subtitle: 'Obtén la app para acceso más fácil',
      description: 'Instala nuestra app para acceso sin conexión, carga más rápida y experiencia nativa.',
      install: 'Instalar App',
      dismiss: 'Tal vez Después',
      features: [
        'Funciona sin conexión',
        'Carga más rápida',
        'Experiencia como app nativa',
        'Acceso fácil desde pantalla de inicio'
      ]
    }
  };

  const text = content[language];

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-sm">
      <Card className="bg-white shadow-2xl border-2 border-orange-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-lg font-bold text-gray-800">
                  {text.title}
                </CardTitle>
                <p className="text-sm text-gray-600">{text.subtitle}</p>
              </div>
            </div>
            <Button
              onClick={handleDismiss}
              variant="ghost"
              size="sm"
              className="p-1 h-auto"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-sm text-gray-600 mb-4">
            {text.description}
          </p>
          
          <ul className="text-xs text-gray-500 mb-4 space-y-1">
            {text.features.map((feature, index) => (
              <li key={index} className="flex items-center space-x-2">
                <div className="w-1 h-1 bg-orange-400 rounded-full"></div>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          
          <div className="flex space-x-2">
            <Button
              onClick={handleInstallClick}
              className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              {text.install}
            </Button>
            <Button
              onClick={handleDismiss}
              variant="outline"
              className="border-gray-300"
            >
              {text.dismiss}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PWAInstallPrompt;